import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalTemplate from "../../modal-template";
import { createClient, Video } from "pexels";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import Player from "./player";
import styled from "styled-components";
import Crop from "./crop";

interface IProps {
  videoId: number;
  onClose: () => void;
}

const ClipCanvasVideo = ({ videoId, onClose }: IProps) => {
  const PexelsApiKey = process.env.REACT_APP_PIXELS_API;
  const client = createClient(PexelsApiKey || "");
  const [clipped, setClipped] = useState(false);
  const [video, setVideo] = useState<Video>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | MediaSource>();
  const [isSaving, setIsSaving] = useState(false);
  const [videoRange, setVideoRange] = useState({ startTime: 0, endTime: 3 });

  const ShowTwoBtns = useMemo(() => {
    if (isSaving) {
      return false;
    }
    if (clipped) {
      return false;
    }
    return true;
  }, [clipped, isSaving]);

  const OnDownload = () => {
    if (!croppedBlob) {
      return;
    }
    const url = URL.createObjectURL(croppedBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Rated-Afrika-${new Date().toISOString()}.webm`;
    a.click();
    setCroppedBlob(undefined);
    onClose();
  };

  const OnNext = () => {
    setIsSaving(true);
  };

  const [isPlaying, setIsPlaying] = useState(true);
  const [duration, setDuration] = useState(0);

  const ToggleVideo = () => {
    if (isPlaying) {
      setIsPlaying(false);
      videoRef.current?.pause();
    } else {
      setIsPlaying(true);
      videoRef.current?.play();
    }
  };

  const handleLoadedMetadata = (event: any) => {
    const video = event.currentTarget;
    const videoDuration = event.target.duration;
    setDuration(videoDuration);

    video.currentTime = videoRange.startTime;
    video.play();

    const startTime = videoRange.startTime;
    const endTime = videoRange.endTime;

    const loopHandler = () => {
      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
        video.play(); // just in case it pauses due to buffering
      }
    };

    video.addEventListener("timeupdate", loopHandler);

    // Optional cleanup if unmounting (for React):
    video.onpause = () => {
      video.removeEventListener("timeupdate", loopHandler);
    };
  };

  useEffect(() => {
    const GetVideo = async () => {
      try {
        const response = await client.videos.show({ id: videoId });

        if (response) {
          setVideo(response);
        }
      } catch (err) {
        alert(`Something went work ${err}`);
      }
    };

    if (videoId !== 0) {
      GetVideo();
    }
  }, [videoId]);

  useEffect(() => {
    if (!isSaving && croppedBlob) {
      setClipped(true);
    }
  }, [isSaving, croppedBlob]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const { startTime, endTime } = videoRange;

    const loopHandler = () => {
      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
        video.play();
      }
    };

    video.addEventListener("timeupdate", loopHandler);

    return () => {
      video.removeEventListener("timeupdate", loopHandler);
    };
  }, [videoRange]);

  return (
    <ModalTemplate
      header="Clip your canvas video"
      twoButtons={ShowTwoBtns}
      close={() => onClose()}
      cancelText="Cancel"
      confirmText={clipped ? "Download" : isSaving ? "Saving" : "Next"}
      loading={isSaving}
      confirm={
        clipped ? () => OnDownload() : isSaving ? () => {} : () => OnNext()
      }
    >
      <Typography align="center" color="white">
        Spotify requires your canvas to be a{" "}
        <span style={{ color: Colors.primary }}> rectangular video</span>,{" "}
        <br /> lasting between{" "}
        <span style={{ color: Colors.primary }}>3 to 8 seconds</span>.
      </Typography>
      {croppedBlob ? (
        <CroppedVideoContainer>
          <CroppedVideo
            src={URL.createObjectURL(croppedBlob)}
            loop
            muted
            autoPlay
            playsInline
          />
        </CroppedVideoContainer>
      ) : (
        <>
          {video && (
            <VideoContainer>
              <Crop
                link={video.video_files[0].link}
                setCroppedBlob={(blob) => setCroppedBlob(blob)}
                isSaving={isSaving}
                setIsSaving={(bool) => setIsSaving(bool)}
                videoRange={videoRange}
              >
                <VideoItem
                  ref={videoRef}
                  src={video.video_files[0].link}
                  muted
                  loop
                  playsInline
                  autoPlay
                  crossOrigin="anonymous"
                  onLoadedMetadata={handleLoadedMetadata}
                />
              </Crop>
            </VideoContainer>
          )}
          {duration ? (
            <Player
              isPlaying={isPlaying}
              toggleVideo={ToggleVideo}
              duration={duration}
              setRange={({ startTime, endTime }) =>
                setVideoRange({ startTime, endTime })
              }
            />
          ) : (
            <></>
          )}
        </>
      )}
    </ModalTemplate>
  );
};

export default ClipCanvasVideo;

const VideoContainer = styled.div`
  width: 37rem;
  height: 37rem;
  position: relative;
  margin-top: 2.4rem;
  margin-left: auto;
  margin-right: auto;
  background-color: yellow;
`;

const VideoItem = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 37rem;
  height: 37rem;
  object-fit: cover;
  background-color: ${Colors.grey500};
`;

const CroppedVideoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CroppedVideo = styled(VideoItem)`
  position: relative;
  width: 22rem;
  height: 37rem;
  object-fit: contain;
  margin-top: 2.4rem;
  margin-left: auto;
  margin-right: auto;
`;
